// Pure helpers for a terraform job's module inventory (getStackModules).

import { StackModule, TerraformStack } from '../../types/DeploymentRecord';
import { sortByEnvRegion } from './rollbackProfiles';

export const stackModulesKey = (app: string, jobId: string): string => `${app}/${jobId}`;

/** A local (`../`) module has no registry version. */
export const versionLabel = (version: string | undefined): string => version || 'local';

export const shortCommit = (sha: string | undefined): string => (sha ? sha.slice(0, 7) : '');

const REGISTRY_HOSTS = ['registry.opentofu.org/', 'registry.terraform.io/'];

export const providerLabel = (address: string): string => {
  const host = REGISTRY_HOSTS.find(h => address.startsWith(h));
  return host ? address.slice(host.length) : address;
};

export const sortStacks = (stacks: TerraformStack[]): TerraformStack[] => sortByEnvRegion(stacks);

/**
 * The backend splits a row too big for one frame into pieces that share its
 * env_region; join them back in arrival order.
 */
export const mergeStackPieces = (pieces: TerraformStack[]): TerraformStack[] => {
  const byEnv = new Map<string, TerraformStack>();
  pieces.forEach(p => {
    const held = byEnv.get(p.env_region);
    if (!held) {
      byEnv.set(p.env_region, {
        ...p,
        modules: [...(p.modules || [])],
        providers: [...(p.providers || [])],
        module_sources: [...(p.module_sources || [])],
      });
      return;
    }
    held.modules.push(...(p.modules || []));
    held.providers.push(...(p.providers || []));
    (p.module_sources || []).forEach(src => {
      if (!held.module_sources.includes(src)) held.module_sources.push(src);
    });
  });
  return Array.from(byEnv.values());
};

/** Version per env_region; a missing key means the env does not have it. */
export type VersionsByEnv = Record<string, string>;

export interface ModuleMatrixRow {
  key: string;
  /** Nesting under the module that declares it; 0 for the stack's own calls. */
  depth: number;
  source: string;
  constraint: string;
  versions: VersionsByEnv;
  differs: boolean;
}

export interface ProviderMatrixRow {
  address: string;
  versions: VersionsByEnv;
  differs: boolean;
}

export interface Matrix<R> {
  columns: string[];
  rows: R[];
}

// An env without the entry counts as a difference too.
const differs = (versions: VersionsByEnv, columns: string[]): boolean => {
  const present = columns.filter(c => c in versions);
  return present.length < columns.length || new Set(present.map(c => versions[c])).size > 1;
};

export const buildModuleMatrix = (stacks: TerraformStack[]): Matrix<ModuleMatrixRow> => {
  const sorted = sortStacks(stacks);
  const columns = sorted.map(s => s.env_region);
  // Source, constraint and parent come from the first env (ladder order) that has the module.
  const first = new Map<string, StackModule>();
  const versions = new Map<string, VersionsByEnv>();
  sorted.forEach(s =>
    (s.modules || []).forEach(m => {
      if (!first.has(m.key)) first.set(m.key, m);
      versions.set(m.key, {
        ...(versions.get(m.key) || {}),
        [s.env_region]: m.version || '',
      });
    })
  );

  const children = new Map<string, string[]>();
  const roots: string[] = [];
  first.forEach((m, key) => {
    const parent = m.declared_in;
    if (parent && parent !== 'root' && parent !== key && first.has(parent)) {
      children.set(parent, [...(children.get(parent) || []), key]);
    } else {
      roots.push(key);
    }
  });

  const rows: ModuleMatrixRow[] = [];
  const seen = new Set<string>();
  const visit = (key: string, depth: number) => {
    if (seen.has(key)) return;
    seen.add(key);
    const m = first.get(key)!;
    const v = versions.get(key) || {};
    rows.push({
      key,
      depth,
      source: m.source,
      constraint: m.constraint,
      versions: v,
      differs: differs(v, columns),
    });
    [...(children.get(key) || [])].sort().forEach(child => visit(child, depth + 1));
  };
  roots.sort().forEach(key => visit(key, 0));
  // A declared_in cycle has no root; still show its members.
  Array.from(first.keys())
    .sort()
    .forEach(key => visit(key, 0));
  return { columns, rows };
};

export const buildProviderMatrix = (stacks: TerraformStack[]): Matrix<ProviderMatrixRow> => {
  const sorted = sortStacks(stacks);
  const columns = sorted.map(s => s.env_region);
  const versions = new Map<string, VersionsByEnv>();
  sorted.forEach(s =>
    (s.providers || []).forEach(p => {
      versions.set(p.address, {
        ...(versions.get(p.address) || {}),
        [s.env_region]: p.version,
      });
    })
  );
  const rows = Array.from(versions.keys())
    .sort()
    .map(address => {
      const v = versions.get(address)!;
      return { address, versions: v, differs: differs(v, columns) };
    });
  return { columns, rows };
};
